import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { LETTERS, LETTER_ORDER } from "@/data/letters";
import { analyzeStrokes, TOLERANCE_MODES } from "@/lib/scoring";
import { TracingCanvas } from "@/components/tracing/TracingCanvas";
import { ScoreDisplay } from "@/components/ScoreDisplay";
import { Confetti } from "@/components/Confetti";
import { MissionComplete } from "@/components/MissionComplete";
import { MusicToggle } from "@/components/MusicToggle";
import { startMusic, stopMusic, playCheer } from "@/lib/sfx";
import {
  Play, Pause, RotateCcw, Undo2, Eraser, Eye, CheckCircle2,
  ArrowLeft, Sparkles, MapPin,
} from "lucide-react";

// Stage configuration — Watch -> Trace -> Practice -> Independent
const STAGES = {
  watch: { key: "watch", title: "Stage 1 — Watch", label: "Watch" },
  trace: {
    key: "trace", title: "Stage 2 — Trace", label: "Trace It",
    guideOpacity: 0.4, showLetter: true, showStart: true,
    tolerance: TOLERANCE_MODES.standard, threshold: 70, attemptType: "trace",
    instruction: "Trace the letter by starting at the dot and following the line.",
    next: "practice", nextLabel: "Practice It Yourself",
  },
  practice: {
    key: "practice", title: "Stage 3 — Practice It Yourself", label: "Practice",
    guideOpacity: 0.15, showLetter: true, showStart: true,
    tolerance: TOLERANCE_MODES.standard, threshold: 75, attemptType: "practice",
    instruction: "The guide is faint now. Write over it carefully.",
    next: "independent", nextLabel: "Write It Yourself",
  },
  independent: {
    key: "independent", title: "Stage 4 — Write It Yourself", label: "Write It",
    guideOpacity: 0, showLetter: false, showStart: false,
    tolerance: TOLERANCE_MODES.standard + 2, threshold: 80, attemptType: "independent",
    instruction: "No guide this time — write the letter from memory on the lines!",
    next: null, nextLabel: "Finish",
  },
};

const STAGE_FLOW = ["watch", "trace", "practice", "independent"];

function ControlButton({ onClick, disabled, testId, children, variant = "default" }) {
  const styles = {
    default: "bg-white border-2 border-[#E5DECF] text-[#1F2937] hover:border-[#0D9488] hover:bg-[#F2ECE1]",
    primary: "bg-[#0D9488] text-white shadow-md hover:bg-[#0B7F74] hover:shadow-lg",
    accent: "bg-[#F59E0B] text-white shadow-md hover:bg-[#D97706] hover:shadow-lg",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-base font-bold transition-all active:translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed ${styles[variant]}`}
    >
      {children}
    </button>
  );
}

export default function Lesson() {
  const { letterId } = useParams();
  const navigate = useNavigate();
  const { user, refreshProgress } = useApp();
  const letter = LETTERS[letterId];

  const canvasRef = useRef(null);
  const animatorRef = useRef(null);
  const [stage, setStage] = useState("watch");
  const [strokeCount, setStrokeCount] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);
  const [showScore, setShowScore] = useState(false);
  const [passed, setPassed] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [missionComplete, setMissionComplete] = useState(false);
  const [checking, setChecking] = useState(false);
  const [lastResp, setLastResp] = useState(null);

  const cfg = STAGES[stage];
  const stageIndex = STAGE_FLOW.indexOf(stage);

  // Playful ukulele background loop while the letter lesson is open.
  useEffect(() => {
    startMusic();
    return () => stopMusic();
  }, []);

  // Cheer + applause every time confetti pops.
  useEffect(() => { if (celebrate) playCheer(); }, [celebrate]);

  const nextLetter = useMemo(() => {
    if (!letter) return null;
    const idx = LETTER_ORDER.indexOf(letter.id);
    return LETTER_ORDER[idx + 1] || null;
  }, [letter]);

  if (!letter) {
    return <div className="p-10 text-center">Unknown letter.</div>;
  }

  const resetCanvas = () => {
    canvasRef.current?.clear();
    setStrokeCount(0);
  };

  const handleCheck = async () => {
    const strokes = canvasRef.current?.getStrokes() || [];
    if (strokes.length === 0) return;
    setChecking(true);
    const res = analyzeStrokes(strokes, letter, { tolerance: cfg.tolerance });
    setResult(res);
    const didPass = res.score >= cfg.threshold;
    setPassed(didPass);
    setShowScore(true);

    try {
      const resp = await api.createAttempt({
        student_id: user.id,
        letter: letter.id,
        attempt_type: cfg.attemptType,
        score: res.score,
        path_accuracy: res.pathAccuracy,
        completion_score: res.completion,
        starting_position_score: res.startingPosition,
        direction_score: res.direction,
      });
      setLastResp(resp);
      await refreshProgress();
    } catch (e) {
      setLastResp(null);
      toast.error("Couldn't save your progress. Please check your connection and try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleContinue = () => {
    setShowScore(false);
    if (stage === "independent") {
      if (lastResp?.mission_complete) {
        setMissionComplete(true);
      }
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 4200);
      resetCanvas();
      return;
    }
    // advance stage
    resetCanvas();
    setStage(cfg.next);
  };

  const handleRetry = () => {
    setShowScore(false);
    resetCanvas();
  };

  const goToStage = (s) => {
    resetCanvas();
    animatorRef.current?.reset();
    setPlaying(false);
    setStage(s);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      <button
        onClick={() => navigate("/")}
        data-testid="back-to-missions"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#0D9488]"
      >
        <ArrowLeft size={16} /> Back to Missions
      </button>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Cursive Mission</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">
            Learn {letter.displayName}
          </h1>
        </div>
        {/* stage stepper + music toggle */}
        <div className="flex items-center gap-3">
          <MusicToggle />
          <div className="flex items-center gap-1.5" data-testid="stage-stepper">
            {STAGE_FLOW.map((s, i) => (
              <div
                key={s}
                className={`h-2.5 rounded-full transition-all ${
                  i <= stageIndex ? "w-8 bg-[#0D9488]" : "w-4 bg-[#E5DECF]"
                }`}
                title={STAGES[s].label}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* Left: handwriting surface */}
        <div className="rounded-[28px] border border-[#E5DECF] bg-white p-4 sm:p-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-[#1F2937]" data-testid="stage-title">{cfg.title}</h2>
            {stage !== "watch" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F2ECE1] px-3 py-1 text-xs font-bold text-[#6B7280]">
                <MapPin size={12} /> {cfg.showStart ? "Start at the dot" : "Freehand"}
              </span>
            )}
          </div>

          <div className="rounded-3xl bg-[#FAF7F2] p-2 ring-1 ring-[#E5DECF]">
            <TracingCanvas
              ref={canvasRef}
              letter={letter}
              animatorRef={animatorRef}
              drawingEnabled={stage !== "watch"}
              guideOpacity={stage === "watch" ? 0.5 : cfg.guideOpacity}
              showStart={stage === "watch" ? true : cfg.showStart}
              showLetterGuide={stage === "watch" ? true : cfg.showLetter}
              onStrokesChange={(s) => setStrokeCount(s.length)}
            />
          </div>

          {/* Controls */}
          {stage === "watch" ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <ControlButton
                testId="anim-play-button"
                variant="accent"
                onClick={() => {
                  if (playing) {
                    animatorRef.current?.pause();
                    setPlaying(false);
                  } else {
                    animatorRef.current?.play();
                    setPlaying(true);
                  }
                }}
              >
                {playing ? <Pause size={18} /> : <Play size={18} />} {playing ? "Pause" : "Play"}
              </ControlButton>
              <ControlButton
                testId="anim-replay-button"
                onClick={() => { animatorRef.current?.replay(); setPlaying(true); }}
              >
                <RotateCcw size={18} /> Replay
              </ControlButton>
              <ControlButton testId="ready-to-trace-button" variant="primary" onClick={() => goToStage("trace")}>
                I'm Ready to Trace <CheckCircle2 size={18} />
              </ControlButton>
            </div>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <ControlButton testId="undo-button" disabled={strokeCount === 0} onClick={() => { canvasRef.current?.undo(); }}>
                  <Undo2 size={18} /> Undo
                </ControlButton>
                <ControlButton testId="clear-button" disabled={strokeCount === 0} onClick={resetCanvas}>
                  <Eraser size={18} /> Clear
                </ControlButton>
                <ControlButton
                  testId="show-me-button"
                  onClick={() => { animatorRef.current?.replay(); }}
                >
                  <Eye size={18} /> Show Me
                </ControlButton>
                <ControlButton testId="try-again-button" onClick={resetCanvas}>
                  <RotateCcw size={18} /> Try Again
                </ControlButton>
                <ControlButton
                  testId="check-my-writing-button"
                  variant="primary"
                  disabled={strokeCount === 0 || checking}
                  onClick={handleCheck}
                >
                  <CheckCircle2 size={18} /> {checking ? "Checking..." : "Check My Writing"}
                </ControlButton>
              </div>
            </>
          )}
        </div>

        {/* Right: instructions / example */}
        <div className="space-y-6">
          <div className="rounded-[28px] border border-[#E5DECF] bg-white p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">Example</div>
            <div className="mt-2 flex items-center justify-center rounded-2xl bg-[#F2ECE1] py-4">
              <span className="font-cursive text-[#0D9488]" style={{ fontSize: "6rem", lineHeight: 1 }}>{letter.id}</span>
            </div>
          </div>

          <div className="rounded-[28px] border border-[#E5DECF] bg-white p-6">
            <div className="flex items-center gap-2 text-[#0D9488]">
              <Sparkles size={18} />
              <h3 className="font-heading text-lg font-bold">How to do it</h3>
            </div>
            <p className="mt-2 font-body text-[#1F2937]">
              {stage === "watch"
                ? `Watch how ${letter.displayName.toLowerCase()} is written. Press Play to see the pencil trace the letter, then get ready to try it yourself.`
                : cfg.instruction}
            </p>
            <div className="mt-4 rounded-2xl bg-[#F2ECE1] p-4 text-sm font-body text-[#6B7280]">
              <div className="font-bold text-[#1F2937] mb-1">The learning path</div>
              Watch → Trace → Practice → Write It Yourself → Master
            </div>
            {stage !== "watch" && (
              <div className="mt-3 text-sm text-[#6B7280]">
                Reach <span className="font-bold text-[#0D9488]">{cfg.threshold}%</span> to move on.
              </div>
            )}
          </div>

          {/* Placeholder for future ToonTeacher animated lesson */}
          <div className="rounded-[28px] border border-dashed border-[#E5DECF] bg-[#F2ECE1]/40 p-5 text-center">
            <div className="text-xs uppercase tracking-widest font-bold text-[#94A3B8]">Coming Soon</div>
            <div className="mt-1 font-heading font-bold text-[#6B7280]">Animated Video Lesson</div>
          </div>
        </div>
      </div>

      <ScoreDisplay
        open={showScore}
        result={result}
        passed={passed}
        mastered={passed && stage === "independent"}
        onRetry={handleRetry}
        onContinue={handleContinue}
        continueLabel={cfg.nextLabel}
      />

      {celebrate && <Confetti />}

      {missionComplete && (
        <MissionComplete
          onPracticeAgain={() => { setMissionComplete(false); navigate("/"); }}
          onContinue={() => { setMissionComplete(false); navigate("/progress"); }}
        />
      )}

      {/* Mastery banner (single-letter, not full mission) */}
      {celebrate && !missionComplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="fixed inset-x-0 bottom-8 z-50 mx-auto w-fit rounded-full bg-[#10B981] px-6 py-3 text-white font-bold shadow-xl"
          data-testid="letter-mastered-banner"
        >
          🌟 Letter Mastered! {nextLetter ? `Lowercase ${nextLetter} unlocked!` : ""}
        </motion.div>
      )}
    </div>
  );
}
